import { useCallback, useState } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shared/widgets/carousel';
import { cn } from '@/shared/utils/cn';
import { resolveImageUrl } from '@/shared/utils/imageUtils';
import customerSpeakData from './data/customerSpeakTestimonials.json';

export interface CustomerSpeakTestimonial {
  id: string;
  productName: string;
  productImageUrl: string;
  quote: string;
  customerName: string;
}

export interface CustomerSpeakWidgetProps {
  testimonials?: CustomerSpeakTestimonial[];
  title?: string;
  className?: string;
}

function CarouselArrow({
  direction,
  onClick,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
}) {
  const label = direction === 'prev' ? 'Previous testimonial' : 'Next testimonial';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'absolute top-1/2 z-10 -translate-y-1/2 text-[#334e80] transition-opacity hover:opacity-70',
        direction === 'prev' ? 'left-1' : 'right-1'
      )}
    >
      <svg
        aria-hidden
        width="10"
        height="16"
        viewBox="0 0 10 16"
        fill="currentColor"
        className={cn(direction === 'next' && 'rotate-180')}
      >
        <path d="M8.5 1.5L1.5 8l7 6.5V1.5z" />
      </svg>
    </button>
  );
}

function CustomerSpeakCarouselNav({ api }: { api: CarouselApi | undefined }) {
  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  return (
    <>
      <CarouselArrow direction="prev" onClick={scrollPrev} />
      <CarouselArrow direction="next" onClick={scrollNext} />
    </>
  );
}

function CustomerSpeakSlide({ testimonial }: { testimonial: CustomerSpeakTestimonial }) {
  return (
    <div className="flex min-w-0 flex-col items-center px-8 pb-2 pt-4 text-center">
      <AppLink
        to="/testimonials.html"
        target="_blank"
        className="mb-4 block no-underline"
        aria-label={`${testimonial.productName} testimonial`}
      >
        <img
          src={resolveImageUrl(testimonial.productImageUrl)}
          alt={testimonial.productName}
          width={160}
          height={160}
          loading="lazy"
          className="mx-auto block h-40 w-40 bg-[#f0f0f0] object-contain"
        />
      </AppLink>

      <p className="mb-4 w-full break-words font-serif text-sm leading-[1.55] text-[#333]">
        {testimonial.quote}
      </p>

      <span className="mb-3 block h-px w-8 bg-[#334e80]" aria-hidden />

      <p className="text-xs font-bold uppercase tracking-[0.04em] text-[#333]">
        {testimonial.customerName}
      </p>
    </div>
  );
}

export function CustomerSpeakWidget({
  testimonials = customerSpeakData.testimonials,
  title = customerSpeakData.title,
  className,
}: CustomerSpeakWidgetProps) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section
      className={cn('min-w-0 border border-[#e2e2e2] bg-white px-4 py-5', className)}
      aria-label={title}
      data-analytics-section-name={title}
    >
      <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.06em] text-black">{title}</h3>
      <div className="mb-4 border-b border-[#333]" aria-hidden />

      <Carousel opts={{ loop: true }} setApi={setCarouselApi} className="relative min-w-0">
        <CustomerSpeakCarouselNav api={carouselApi} />
        <CarouselContent className="-ml-0">
          {testimonials.map((testimonial) => (
            <CarouselItem key={testimonial.id} className="basis-full pl-0">
              <CustomerSpeakSlide testimonial={testimonial} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  );
}
